// linked-variant-picker.js > LinkedVariantPicker (custom element for Gottex)
// adapted from theme.js > _VariantPicker

function deepQuerySelector(root, selector) {
  let element = root.querySelector(selector);
  if (element) {
    return element;
  }
  for (const template of root.querySelectorAll("template")) {
    element = deepQuerySelector(template.content, selector);
    if (element) {
      return element;
    }
  }
  return null;
}

import { Delegate } from "vendor";
var _preloadedHtml, _delegate, _intersectionObserver, _form, _selectedVariant, _LinkedVariantPicker_instances, getActiveOptionValues_fn, getOptionValuesFromOption_fn, onOptionChanged_fn, onOptionPreload_fn, onIntersection_fn, renderForCombination_fn, createHashKeyForHtml_fn;
var LinkedVariantPicker = class LinkedVariantPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    this._LinkedVariantPicker_instances = new WeakSet();
    this._delegate = new Delegate(document.body);
    this._intersectionObserver = new IntersectionObserver(onIntersection_fn.bind(this))
    this._form = new WeakMap();
    this._selectedVariant = new WeakMap();
    this._preloadedHtml = new WeakMap();
  }
  async connectedCallback() {
    this._selectedVariant = JSON.parse(this.querySelector("script[data-variant]")?.textContent || "{}");
    this._form = document.forms[this.getAttribute("form-id")];
    this._delegate.on("change", `input[data-option-position][form="${this.getAttribute("form-id")}"]`, onOptionChanged_fn.bind(this));
    this._delegate.on("pointerenter", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, onOptionPreload_fn.bind(this), true);
    this._delegate.on("touchstart", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, onOptionPreload_fn.bind(this), true);
    this._intersectionObserver.observe(this);
  }
  disconnectedCallback() {
    this._delegate.off();
    this._intersectionObserver.unobserve(this);
  }
  get selectedVariant() {
    return this._selectedVariant;
  }
  get productHandle() {
    return this.getAttribute("handle");
  }
  get updateUrl() {
    return this.hasAttribute("update-url");
  }
  /**
   * Select a variant using a list of option values. The list of option values might lead to no variant (for instance)
   * in the case of a combination that does not exist
   */
  async selectCombination({ optionValues, productChange, productUrl }) {
    //window.console.log('~~~selectCombination', productChange, productUrl, optionValues);
    const previousVariant = this.selectedVariant;
    const newContent = document.createRange().createContextualFragment(await renderForCombination_fn.call(this, optionValues));
    //if (!productChange) {
      const newVariantPicker = deepQuerySelector(newContent, `${this.tagName}[form-id="${this.getAttribute("form-id")}"]`);
      const newVariant = JSON.parse(newVariantPicker.querySelector("script[data-variant]")?.textContent || "{}");
      this._selectedVariant = newVariant;
      this._form.id.value = this._selectedVariant?.id;
      this._form.id.dispatchEvent(new Event("change", { bubbles: true }));
      if (this.updateUrl && this._selectedVariant?.id) {
        const newUrl = productUrl ? new URL(productUrl, window.location) : new URL(window.location.href);
        newUrl.searchParams.set("variant", this._selectedVariant.id);
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }
    //}

    this._form.dispatchEvent(new CustomEvent("product:rerender", {
      detail: {
        htmlFragment: newContent,
        productChange
      }
    }));
    if (!productChange) {
      this._form.dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          formId: this._form.id,
          variant: this._selectedVariant,
          previousVariant
        }
      }));
    }

    if (productChange) {
      document.documentElement.dispatchEvent(new CustomEvent("linkedvariantpicker:swatch:change", { 
        bubbles: true,
        detail: {
          productUrl: productUrl
        }
      }));
    }

    Shopify?.PaymentButton?.init();

    // swym wishlist https://developers.getswym.com/docs/swym-powered-buttons
    if (typeof _swat != 'undefined') {
      var productContainerId = '#' + this.closest('product-rerender').id;
      _swat.initializeActionButtons(productContainerId, '[data-swaction]');
    }

    // yotpo reviews
    //if (typeof yotpoWidgetsContainer != 'undefined') {
      // not working, must be an older yotpo version
      //yotpoWidgetsContainer.initWidgets();
    //}
    if (typeof yotpo != 'undefined') {
      yotpo.initWidgets();
    }
  }
};

/**
 * Get the option values for the active combination
 */
getActiveOptionValues_fn = function() {
  const activeValues = Array.from(this._form.elements).filter((item) => item.matches("input[data-option-position]:checked")).sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input) => input.value);
  return activeValues;
};
/**
 * Get the option values for a given input
 */
getOptionValuesFromOption_fn = function(input) {
  const optionValues = [input, ...Array.from(this._form.elements).filter((item) => item.matches(`input[data-option-position]:not([name="${input.name}"]):checked`))].sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input2) => input2.value);
  return optionValues;
};
onOptionChanged_fn = async function(event) {
  if (!event.target.name.includes("option")) {
    return;
  }
  this.selectCombination({
    optionValues: getActiveOptionValues_fn.call(this),
    productChange: event.target.hasAttribute("data-product-url"),
    productUrl: event.target.getAttribute("data-product-url")
  });
};
/**
 * To improve the user experience, we preload a variant whenever the user hovers over a specific option
 */
onOptionPreload_fn = function(event, target) {
  renderForCombination_fn.call(this, getOptionValuesFromOption_fn.call(this, target.control));
};
/**
 * When the variant picker is intersecting the viewport, we preload the options to improve the user experience
 * so that switching variants is nearly instant
 */
onIntersection_fn = function(entries) {
  const prerenderOptions = () => {
    Array.from(this._form.elements).filter((item) => item.matches("input[data-option-position]:not(:checked)")).forEach((input) => {
      renderForCombination_fn.call(this, getOptionValuesFromOption_fn.call(this, input));
    });
  };
  if (entries[0].isIntersecting) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(prerenderOptions, { timeout: 2e3 });
    } else {
      prerenderOptions();
    }
  }
};
renderForCombination_fn = async function(optionValues) {
  const optionValuesAsString = optionValues.join(",");
  const hashKey = createHashKeyForHtml_fn.call(this, optionValuesAsString);
  let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;
  for (const optionValue of optionValues) {
    const inputForOptionValue = Array.from(this._form.elements).find((item) => item.matches(`input[value="${optionValue}"]`));
    if (inputForOptionValue?.dataset.productUrl) {
      productUrl = inputForOptionValue.dataset.productUrl;
      break;
    }
  }
  if (!LinkedVariantPicker._preloadedHtml.has(hashKey)) {
    const sectionQueryParam = this.getAttribute("context") === "quick_buy" ? "" : `&section_id=${this.getAttribute("section-id")}`;
    const promise = new Promise(async (resolve) => {
      resolve(await (await fetch(`${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`)).text());
    });
    LinkedVariantPicker._preloadedHtml.set(hashKey, { htmlPromise: promise, timestamp: Date.now() });
    if (LinkedVariantPicker._preloadedHtml.size > 100) {
      LinkedVariantPicker._preloadedHtml.delete(Array.from(LinkedVariantPicker._preloadedHtml.keys())[0]);
    }
  }
  return LinkedVariantPicker._preloadedHtml.get(hashKey).htmlPromise;
};
createHashKeyForHtml_fn = function(optionValuesAsString) {
  return `${optionValuesAsString}-${this.getAttribute("section-id")}`;
};
LinkedVariantPicker._preloadedHtml = new Map();
var LinkedVariantPicker = LinkedVariantPicker;
if (!window.customElements.get("linked-variant-picker")) {
  window.customElements.define("linked-variant-picker", LinkedVariantPicker);
}

export {
    LinkedVariantPicker
};